import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService, TreeNode } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { Profile } from 'src/app/domain/models/Profile';
import { BasicUser } from 'src/app/domain/models/User';
import { Routes } from 'src/app/infrastructure/routes';
import { rutVerifierDigit } from 'src/app/ui/util/custom-validators';
import { formLimit } from 'src/app/ui/util/form-limit';
import { ProfileUtils } from 'src/app/ui/util/profile-utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-user-company-info',
  templateUrl: './user-company-info.component.html',
  styleUrls: ['./user-company-info.component.css'],

})
export class UserCompanyInfoComponent implements OnInit {

  uploadedFiles: any[] = []
  products: any
  selectedProducts: TreeNode | undefined
  submit: boolean = false
  loading: boolean = false
  disabledFields: boolean = true

  profile: Profile | undefined
  formLimit = formLimit;

  profileUser: BasicUser | undefined

  breadcrumbMenuItems: MenuItem[] = [{ label: '' }]
  breadcrumbHome: MenuItem = { label: ''}

  exporterLogo: any[] = []

  formGroup: FormGroup = this._formBuilder.group({
    companyName: [{value: '', disabled: true}, [Validators.required, Validators.maxLength(50)]],
    vat: [{value: null, disabled: true}, [Validators.required, Validators.maxLength(25)]],
    sag: [{value: null, disabled: true}],
    legalRepresentative: [{value: '', disabled: true}, Validators.required],
    legalRepresentativeId: [{value: '', disabled: true}, [Validators.required, Validators.maxLength(25)]],
    logo: [{value: null, disabled: true}],
    pdf: [{value: null, disabled: true}]
  })


  constructor(private _router: Router,
    private _formBuilder: FormBuilder,
    private _messageService: MessageService,
    private _authService: AuthGateway,
    private _profileService: ProfileGateway,
    public translate: TranslateService) {
  }

  ngOnInit(): void {

    this.profileUser = this._authService.getUser()

    let breadcrumbStep1ItemLabel1 = 'Company info'
    let homeItemLabel = 'User panel'

    if(this.profileUser) {
      switch(this.profileUser.lang) {
        case 'en':
          breadcrumbStep1ItemLabel1 = 'Company info'
          homeItemLabel = 'User panel'
          break
        case 'es':
          breadcrumbStep1ItemLabel1 = 'Datos de la empresa'
          homeItemLabel = 'Panel de usuario'
          break
      }
    }

    this.breadcrumbMenuItems = [{ label: breadcrumbStep1ItemLabel1, disabled: true }]
    this.breadcrumbHome = { label: homeItemLabel, routerLink: '/' + Routes.userPanelPage}


    this._profileService.getByUsername().subscribe({
      next: (v) => {
        this.profile = v
        this.loadForm()
        this.formGroup.controls['vat'].addValidators(rutVerifierDigit(this.profile.type, this.profile.contactCountryId))
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ProfileMessageService.SingleRetrieveError') });
      },
    })
  }

  loadForm() {
    if(this.profile) {
      this.formGroup.controls["companyName"].setValue(this.profile.companyName)
      this.formGroup.controls["vat"].setValue(this.profile.vat)
      this.formGroup.controls["sag"].setValue(this.profile.sagCode)
      this.formGroup.controls["legalRepresentative"].setValue(this.profile.legalRepresentativeName)
      this.formGroup.controls["legalRepresentativeId"].setValue(this.profile.legalRepresentativeNumber)
      this.formGroup.controls["logo"].setValue(this.profile.logoUrl)
      this.formGroup.controls["pdf"].setValue(this.profile.pdfUrl)
    }
  }

  validateSII() {
    /*
    if(this.profile && this.profile.contactCountry.code == 'CL' && !this.formGroup.getError('verifierDigitError')) {
      let rut = this.formGroup.controls["vat"].value
      this._profileService.validateSII(rut).subscribe({
        next: (v) => {
          console.log(v)
        },
        error: (e) => {
          console.log(e)
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'Error validando el RUT en SII API' });
        },
        complete: () => console.info('VAT validation query successfully')
      })
    }*/
  }

  save(): void {
    this.loading = true
    this.submit = true

    if(this.formGroup.valid && this.profile) {
      this.profile.companyName = this.formGroup.controls["companyName"].value
      this.profile.vat = this.formGroup.controls["vat"].value
      this.profile.sagCode = this.formGroup.controls["sag"].value
      this.profile.legalRepresentativeName = this.formGroup.controls["legalRepresentative"].value
      this.profile.legalRepresentativeNumber = this.formGroup.controls["legalRepresentativeId"].value
      this.profile.updaterUsername = this._authService.getUsername()
      this.profile.pdf = this.uploadedFiles[0]

      if (this.profile.type === 2 || this.profile.type === 5) {
        this.profile.logo = this.exporterLogo?.length > 0 ? this.exporterLogo[0] : null
      }

      this._profileService.update(this.profile).subscribe({
        next: (v) => {
          this.profile = v
          this._messageService.add({ severity: 'success',
            summary: this.translate.instant('MessageService.Summary.SuccessAction'),
            detail: this.translate.instant('MessageService.ProfileMessageService.EditSuccess') });
          this.loadForm()
          this.submit = false
          this.loading = false
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.ProfileMessageService.EditError') });
        },
      })
    }
    else {
      this.loading = false
    }
  }

  setEnableEdit() {
    this.disabledFields = !this.disabledFields
    let fields = ["companyName", "vat", "sag", "legalRepresentative", "legalRepresentativeId"]

    if(this.disabledFields) {
      fields.forEach(x => this.formGroup.controls[x].disable())
    }
    else {
      fields.forEach(x => this.formGroup.controls[x].enable())
    }
  }

  getErrorMessage(fieldName: string): string {
    if (this.formGroup.controls[fieldName].hasError('required')) {
      return this.translate.instant('FormErrorMessage.FieldValueRequired')
    }
    if (this.formGroup.controls[fieldName].hasError('email')) {
      return this.translate.instant('FormErrorMessage.EmailFieldEmailFormatError')
    }
    if (this.formGroup.controls[fieldName].hasError('verifierDigitError')) {
      return this.translate.instant('FormErrorMessage.RutError')
    }
    if (this.formGroup.controls[fieldName].hasError('maxlength')) {
      const maxCharacters = this.formGroup.controls[fieldName].errors?.['maxlength']?.requiredLength;
      return this.translate.instant('FormErrorMessage.MaxLengthError', {value: maxCharacters});
    }
    return ''
  }

  get verifierDigitError(): boolean {
    return this.formGroup.getError('verifierDigitError')
  }

  onCancel() {
    this._router.navigate(['/'+Routes.userPanelPage])
  }

  onSelectLogo(event: any) {
    this.exporterLogo = []
    for (let file of event.files) {
      this.exporterLogo.push(file);
    }
  }

  onSelectFile(event: any) {
    this.uploadedFiles = []
    for (let file of event.files) {
      this.uploadedFiles.push(file);
    }
  }

  removeLogo() {
    this.formGroup.get('logo')?.setValue('')
    this.exporterLogo = [];
  }

  removeFile() {
    this.formGroup.get('pdf')?.setValue('')
    this.uploadedFiles = [];
  }

  getFileName(url?: string): string {
    return ProfileUtils.getFileName(url);
  }

  async downloadFile(fileRoute: string) {
    const url = environment.apiUrl + fileRoute
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      this.download(blob);
      return blob;
    } catch (error) {
      throw error;
    }
  }

  download(file: Blob) {
    const link = document.createElement('a');
    const url = URL.createObjectURL(new Blob([file], { type: 'application/pdf' }));
    link.href = url;
    link.target = '_blank'
    link.download = 'Documento.pdf';
    link.click();
  }
}
